import { ChangeEvent, type JSX,RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { FormControl, Stack, Switch, TextField, Theme, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { GridSortModel } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES, ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { selectPaginationGridData, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { getOptionLabelDefaultCallback, isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { PalmDatePicker } from 'common/components/sharedCustomComponents/PalmDatePicker/PalmDatePicker';
import { UserProfileSearchDropdown } from 'common/components/sharedCustomComponents/userProfileSearchDropdown/UserProfileSearchDropdown';
import { fetchAclAccesses } from 'common/services/api/Acls/AclAccesses.thunk';
import { AclSearchPaginationInfo,APIAclEntryPostParams } from 'common/services/api/Acls/AclEntitiesApiTypeDefs';
import { fetchAclResources } from 'common/services/api/Acls/AclResources.thunk';
import { fetchLicense, fetchLicenses } from 'common/services/api/Licenses/Licenses.thunk';
import { fetchUserGroups } from 'common/services/api/UserGroups/UserGroups.thunk';
import { IUser } from 'common/services/api/Users/Users.api';
import {
    Acl,
    AclAccess,
    AclFormatted,
    AclResource,
    AclsWithProfiles,
    EntitiesStatus,
    FormattedAclWithLicenseInfo,
    License,
    LicenseFormatted,
    PrincipalType,
    UserGroupDropdownOption,
    UserGroupWithCreationFlag } from 'common/types/Entities';
import { displayAclResourceMenuItemContent, displayEntityMenuItemContent, filterAclResourceOptionsCustomCallback, filterUserGroupOptionsCustomCallback,getAclResourceOptionLabel } from 'common/utils/AclEntitiesDropDownHelpers';
import { dateToBackendFormat, IDatesValidation, toESTDateString, validateDates } from 'common/utils/dateHelpers';
import { ConflictErrorOrigin } from 'common/utils/LicenseAclEntriesErrorHelpers';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';
import { AclConditionsCreateDropDown, AclConditionsEditDropDown } from 'features/aclConditions/AclConditionsDropDown';
import { CONDITION_VALIDATORS } from 'features/aclConditions/aclConditionsValidation';
import { selectFilters } from 'features/licenseAclEntries/licenseAclEntriesSlice';
import { updateUserProfileTenant,UserProfileTenantUpdatePayload } from 'features/userProfileApps/helpers';

import { AclEntriesDrawerProperties, AclEntriesPage } from './aclEntriesTypeDefs';

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: 'principalName', sort: 'asc' }];

const CREATE_NEW_USER_GROUP_OPTION_LABEL = 'CREATE NEW USER GROUP';

interface BaseStateHandlerResult {
    startDate?: string;
    setStartDate: (startDate?: string) => void;
    endDate?: string;
    setEndDate: (endDate?: string) => void;
    datesValidation: IDatesValidation;
    setDatesValidation: (datesValidation: IDatesValidation) => void;
    paginationInfo: { currentPage?: number; rowsPerPage?: number; sortModel?: GridSortModel };
    currentPage: number;
    rowsPerPage: number;
    sortModel: GridSortModel;
    filterProp: { keywords: string[] };
    resetDates: (startDate?: string, endDate?: string) => void;
    successHandler: (operationResult: { rows: Acl[] | AclsWithProfiles[] | FormattedAclWithLicenseInfo[] }) => void;
    handleSaveBtnEnabled: () => void;
}

function _isUserProfileAclEntriesPage(aclPage: AclEntriesPage): boolean {
    return aclPage === AclEntriesPage.UserProfile;
}

function _getUserProfileTenantUpdatePayload(
    drawerProps: AclEntriesDrawerProperties
): UserProfileTenantUpdatePayload {
    return {
        tenantId: drawerProps.tenantId,
        userId: drawerProps.principalId,
        currentUserProfileTenant: drawerProps?.currentUserProfileTenant
    };
}

function _processCreateEditUserProfileAclEntriesSuccess(
    drawerProps: AclEntriesDrawerProperties
): void {
    const payload = _getUserProfileTenantUpdatePayload(drawerProps);
    updateUserProfileTenant(payload);
}

function useBaseStateHandler(props: AclEntriesDrawerProperties): BaseStateHandlerResult {
    const dispatch = useAppDispatch();
    const [startDate, setStartDate] = useState<string | undefined>();
    const [endDate, setEndDate] = useState<string | undefined>();
    const [datesValidation, setDatesValidation] = useState<IDatesValidation>({
        hasError: false
    });

    const paginationInfo = useAppSelector(selectPaginationGridData) ?? { currentPage: 0, rowsPerPage: 0 };
    const currentPage = paginationInfo.currentPage ?? 0;
    const rowsPerPage = paginationInfo.rowsPerPage ?? 10;
    const sortModel = paginationInfo.sortModel ?? DEFAULT_SORT_MODEL;
    const filterProp = useAppSelector(selectFilters);

    useEffect(() => {
        if(props.tenantRequiresLicense){
            setDatesValidation(validateDates(startDate, endDate, true));
        }
    }, [startDate, endDate]);

    const resetDates = useCallback((newStartDate?: string, newEndDate?: string) => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setDatesValidation({ hasError: false });
    }, []);

    const successHandler = useCallback((operationResult: { rows: Acl[] | AclsWithProfiles[] | FormattedAclWithLicenseInfo[] }) => {
        if (_isUserProfileAclEntriesPage(props.aclPage)) {
            _processCreateEditUserProfileAclEntriesSuccess(props);
        } else {
            dispatch(setRows(operationResult.rows));
        }
    }, [dispatch, props]);

    return {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        datesValidation,
        setDatesValidation,
        paginationInfo,
        currentPage,
        rowsPerPage,
        sortModel,
        filterProp,
        resetDates,
        successHandler,
        handleSaveBtnEnabled: () => {}
    };
}

export interface CreateStateHandlerResult extends BaseStateHandlerResult {
    currentAclResource: AclResource | null;
    setCurrentAclResource: (currentAclResource: AclResource | null) => void;
    currentAclAccess: AclAccess | null;
    setCurrentAclAccess: (currentAclAccess: AclAccess | null) => void;
    currentAclConditionKey: string;
    setCurrentAclConditionKey: (key: string) => void;
    currentAclConditionValue: Record<string, Record<string, string>>;
    setCurrentAclConditionValue: (value: Record<string, Record<string, string>>) => void;
    handleConditionKeyChange: (key: string) => void;
    currentLicense: LicenseFormatted | null;
    setCurrentLicense: (license: LicenseFormatted | null) => void;
    currentUser: IUser | null;
    setCurrentUser: (user: IUser | null) => void;
    currentUserGroup: UserGroupDropdownOption | null;
    setCurrentUserGroup: (userGroup: UserGroupDropdownOption | null) => void;
    principalType: PrincipalType;
    setPrincipalType: (principalType: PrincipalType) => void;
    nameFieldRef: RefObject<HTMLInputElement | undefined>;
    descriptionFieldRef: RefObject<HTMLInputElement | undefined>;
    onOpenChange: () => void;
    handleAclResourceSelect: (_e: unknown, aclResource: AclResource | null) => void;
    handleAclAccessSelect: (_e: unknown, aclAccess: AclAccess | null) => void;
    handleLicenseSelect: (_e: unknown, license: LicenseFormatted | null) => void;
    principalTypeChangeHandler: (_: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    handleUserSelect: (_e: unknown, user: IUser | null) => void;
    handleUserGroupSelect: (_e: unknown, userGroup: UserGroupDropdownOption | null) => void;
    getOptions: () => object;
    getContent: (
        opCallback: () => void,
        processing: boolean,
        nameInConflict: boolean,
        conflictErrorOrigin?: ConflictErrorOrigin
    ) => JSX.Element;
}

export function useCreateStateHandler(
    props: AclEntriesDrawerProperties,
    theme: Theme
): CreateStateHandlerResult {
    const dispatch = useAppDispatch();
    const baseState = useBaseStateHandler(props);
    const isGroup = props.aclPage === AclEntriesPage.TenantUserGroup;

    const [currentAclResource, setCurrentAclResource] = useState<AclResource | null>(null);
    const [currentAclAccess, setCurrentAclAccess] = useState<AclAccess | null>(null);
    const [currentAclConditionKey, setCurrentAclConditionKey] = useState<string>('');
    const [currentAclConditionValue, setCurrentAclConditionValue] = useState<Record<string, Record<string, string>>>({});
    const [currentLicense, setCurrentLicense] = useState<LicenseFormatted | null>(null);
    const [currentUser, setCurrentUser] = useState<IUser | null>(null);
    const [currentUserGroup, setCurrentUserGroup] = useState<UserGroupDropdownOption | null>(null);
    const [principalType, setPrincipalType] = useState<PrincipalType>(PrincipalType.UserGroup);

    const nameFieldRef = useRef<HTMLInputElement>(undefined);
    const descriptionFieldRef = useRef<HTMLInputElement>(undefined);

    const handleConditionKeyChange = useCallback((key: string) => {
        setCurrentAclConditionKey(key);
        setCurrentAclConditionValue({});
    }, []);

    const isConditionValid = useCallback((): boolean => {
        const hasConditions = (currentAclResource?.conditions?.length ?? 0) > 0;
        if (!hasConditions) {
            return true;
        }

        if (!currentAclConditionKey) {
            return false;
        }

        const conditionValue = currentAclConditionValue[currentAclConditionKey];
        const hasValue = conditionValue !== undefined &&
            Object.values(conditionValue).some(val => val !== undefined && val !== '');

        if (!hasValue) {
            return false;
        }

        const validator = CONDITION_VALIDATORS[currentAclConditionKey];
        if (validator) {
            const value = conditionValue?.[validator.parameterName];
            return validator.validate(value);
        }

        return true;
    }, [currentAclResource?.conditions, currentAclConditionKey, currentAclConditionValue]);

    const handleSaveBtnEnabled = useCallback(() => {
        let isButtonEnabled = !!currentAclResource?.id && !!currentAclAccess?.id && isConditionValid();
        if (props.aclPage === AclEntriesPage.License) {
            if (principalType === PrincipalType.User) {
                isButtonEnabled &&= !!currentUser?.id;
            } else if (principalType === PrincipalType.UserGroup) {
                isButtonEnabled &&= !!currentUserGroup?.id;
                if ((currentUserGroup as UserGroupWithCreationFlag)?.shouldDisplayCreateNewUserGroupFields) {
                    isButtonEnabled &&= !!nameFieldRef?.current?.value.trim();
                }
            }
        }
        if (props.tenantRequiresLicense) {
            isButtonEnabled &&= !baseState.datesValidation.hasError;
            if (props.aclPage !== AclEntriesPage.License) {
                isButtonEnabled &&= !!currentLicense?.id;
            }
        }
        dispatch(setSaveButtonEnabled(isButtonEnabled));
    }, [
        dispatch,
        props.aclPage,
        props.tenantRequiresLicense,
        currentAclResource?.id,
        currentAclAccess?.id,
        currentLicense?.id,
        principalType,
        currentUser?.id,
        currentUserGroup,
        baseState.datesValidation.hasError,
        isConditionValid
    ]);

    useEffect(() => {
        handleSaveBtnEnabled();
    }, [
        currentAclResource?.id,
        currentAclAccess?.id,
        currentLicense?.id,
        principalType,
        currentUser,
        currentUserGroup,
        currentAclConditionKey,
        currentAclConditionValue,
        handleSaveBtnEnabled
    ]);

    useEffect(() => {
        handleSaveBtnEnabled();
    }, [baseState.datesValidation, handleSaveBtnEnabled]);

    const onOpenChange = useCallback(() => {
        baseState.resetDates(dateToBackendFormat(toESTDateString(new Date())));
        setCurrentAclResource(null);
        setCurrentAclAccess(null);
        setCurrentAclConditionKey('');
        setCurrentAclConditionValue({});
        setCurrentLicense(null);
        setCurrentUser(null);
        setCurrentUserGroup(null);
        setPrincipalType(PrincipalType.UserGroup);
    }, [baseState]);

    const handleAclResourceSelect = useCallback((_e: unknown, aclResource: AclResource | null) => {
        setCurrentAclResource(aclResource);
        setCurrentAclConditionKey('');
        setCurrentAclConditionValue({});
    }, []);

    const handleAclAccessSelect = useCallback((_e: unknown, aclAccess: AclAccess | null) => {
        setCurrentAclAccess(aclAccess);
    }, []);

    const handleLicenseSelect = useCallback((_e: unknown, license: LicenseFormatted | null) => {
        setCurrentLicense(license);
    }, []);

    const principalTypeChangeHandler = useCallback((_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setPrincipalType(checked ? PrincipalType.User : PrincipalType.UserGroup);
    }, []);

    const handleUserSelect = useCallback((_e: unknown, user: IUser | null) => {
        if (user) {
            setCurrentUser({
                id: user.id,
                profile: {
                    firstName: user.profile.firstName,
                    lastName: user.profile.lastName,
                    principalName: user.profile.principalName,
                    email: user.profile.email,
                    companyKey: user.profile.companyKey
                }
            });
        } else {
            setCurrentUser(null);
        }
        dispatch(setSaveButtonEnabled(!!user));
    }, [dispatch]);

    const handleUserGroupSelect = useCallback((_e: unknown, userGroup: UserGroupDropdownOption | null) => {
        setCurrentUserGroup(userGroup);
    }, []);

    const coalesce = (val?: string) => val ?? '';

    const getOptions = useCallback(() => {
        let paginationInfo: AclSearchPaginationInfo = {};
        const postOptions: APIAclEntryPostParams = {
            tenantId: props.tenantId,
            accessId: coalesce(currentAclAccess?.id),
            resourceId: coalesce(currentAclResource?.id),
            tenantRequiresLicense: props.tenantRequiresLicense
        };
        if (props.aclPage === AclEntriesPage.License) {
            paginationInfo = {
                skip: baseState.currentPage * baseState.rowsPerPage,
                take: baseState.rowsPerPage,
                search: baseState.filterProp.keywords[0],
                sortKey: baseState.sortModel[0].field,
                sortOrder: baseState.sortModel[0].sort
            };
            if (principalType === PrincipalType.User) {
                postOptions.userId = currentUser?.id;
            } else if (principalType === PrincipalType.UserGroup) {
                postOptions.usergroupId = currentUserGroup?.id;
                if ((currentUserGroup as UserGroupWithCreationFlag)?.shouldDisplayCreateNewUserGroupFields) {
                    postOptions.shouldCreateNewUserGroup = true;
                    postOptions.newUserGroupName = nameFieldRef?.current?.value.trim();
                    postOptions.newUserGroupDescription = descriptionFieldRef?.current?.value.trim();
                }
            }
        } else {
            postOptions.userId = isGroup ? undefined : props.principalId;
            postOptions.usergroupId = isGroup ? props.principalId : undefined;
        }
        if (props.tenantRequiresLicense) {
            postOptions.licenseId = (props.aclPage === AclEntriesPage.License)
                ? props.licenseId
                : currentLicense?.id;
            postOptions.startDate = baseState.startDate;
            postOptions.endDate = baseState.endDate;
        }

        // Set condition options if a condition is selected
        if (currentAclConditionKey && currentAclConditionValue[currentAclConditionKey]) {
            postOptions.conditions = [{
                conditionKey: currentAclConditionKey,
                parameters: currentAclConditionValue[currentAclConditionKey]
            }];
        }

        return {
            item: postOptions,
            paginationInfo: paginationInfo,
            aclPage: props.aclPage
        };
    }, [
        props,
        isGroup,
        currentAclAccess?.id,
        currentAclResource?.id,
        currentLicense?.id,
        currentUser?.id,
        currentUserGroup,
        principalType,
        currentAclConditionKey,
        currentAclConditionValue,
        baseState.currentPage,
        baseState.rowsPerPage,
        baseState.filterProp.keywords,
        baseState.sortModel,
        baseState.startDate,
        baseState.endDate
    ]);

    const getContent = useCallback((
        postCallback: () => void,
        processing: boolean,
        nameInConflict: boolean,
        conflictErrorOrigin?: ConflictErrorOrigin
    ) =>
        <EditDrawer
            title='ACL Entry'
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={{ name: '', displayName: '', id: '', tenantId: '', conditions: [] }}
                    value={currentAclResource}
                    onChange={handleAclResourceSelect}
                    label='ACL Resource'
                    getOptionLabel={getAclResourceOptionLabel}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchAclResources}
                    asyncThunkArgs={{ tenantId: props.tenantId }}
                    displayMenuItemContent={displayAclResourceMenuItemContent}
                    filterOptionsCustomCallback={filterAclResourceOptionsCustomCallback}
                    effectDependecyArray={[props.tenantId]}
                />
                {(currentAclResource?.conditions?.length ?? 0) > 0 && (
                    <AclConditionsCreateDropDown
                        conditions={currentAclResource?.conditions}
                        currentConditionKey={currentAclConditionKey}
                        setCurrentConditionKey={handleConditionKeyChange}
                        currentConditionValue={currentAclConditionValue}
                        setCurrentConditionValue={setCurrentAclConditionValue}
                    />
                )}
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={{ name: '', id: '', tenantId: '' }}
                    value={currentAclAccess}
                    onChange={handleAclAccessSelect}
                    label='ACL Access'
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchAclAccesses}
                    asyncThunkArgs={{ tenantId: props.tenantId }}
                    displayMenuItemContent={displayEntityMenuItemContent}
                    effectDependecyArray={[props.tenantId]}
                />
                {props.tenantRequiresLicense && <>
                    {props.aclPage !== AclEntriesPage.License && <EntitiesSearchableCommonDropDown
                        initialEntityOption={{ name: '', id: '', tenantId: '', companyKey: '', note: '', startDate: '', status: EntitiesStatus.INACTIVE }}
                        value={currentLicense}
                        onChange={handleLicenseSelect}
                        label='License'
                        getOptionLabel={getOptionLabelDefaultCallback}
                        isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                        asyncThunkAction={fetchLicenses}
                        asyncThunkArgs={{ tenantId: props.tenantId }}
                        displayMenuItemContent={displayEntityMenuItemContent}
                        effectDependecyArray={[props.tenantId]}
                    />}
                    {props.aclPage === AclEntriesPage.License && <TextField
                        sx={{
                            backgroundColor: 'background.paper',
                            borderRadius: '4px',
                        }}
                        size="small"
                        label="License"
                        variant="filled"
                        defaultValue={props.licenseBillingSystemName + ' - ' + props.licenseBillingRecord}
                        disabled
                    />}
                    <Stack
                        display="flex"
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <PalmDatePicker
                                label='Start Date'
                                required
                                onChange={(value?: string) => baseState.setStartDate(value)}
                                value={baseState.startDate}
                            ></PalmDatePicker>
                            <PalmDatePicker
                                label='End Date'
                                onChange={(value?: string) => baseState.setEndDate(value)}
                                value={baseState.endDate}
                            ></PalmDatePicker>
                        </LocalizationProvider>
                    </Stack>
                    {baseState.datesValidation.hasError && (
                        <ErrorHandler
                            component='div'
                            display='inline'
                            errorMessage={baseState.datesValidation.errorMessage}
                        />
                    )}
                </>}
                {
                    props.aclPage === AclEntriesPage.License &&
                    <>
                        <FormControl sx={{
                            flexDirection: 'row',
                            alignItems: 'center'
                        }}>
                            <Typography sx={{
                                marginRight: '10px',
                                fontWeight: 'bold'
                            }}>
                                Principal type:
                            </Typography>
                            <Typography
                                component='span'
                                color={theme.palette.primary.accent}>
                                User Group
                            </Typography>
                            <Switch
                                sx={{
                                    '& .MuiSwitch-switchBase': {
                                        '&.Mui-checked': {
                                            '& + .MuiSwitch-track': {
                                                backgroundColor: theme.palette.primary.accent,
                                                opacity: 0.5,
                                            }
                                        }
                                    },
                                    '& .MuiSwitch-thumb': {
                                        backgroundColor: theme.palette.primary.accent,
                                        opacity: 1
                                    },
                                    '& .MuiSwitch-track': {
                                        backgroundColor: theme.palette.primary.accent,
                                        opacity: 0.5
                                    }
                                }}
                                value={principalType === PrincipalType.UserGroup}
                                onChange={principalTypeChangeHandler}
                            />
                            <Typography
                                component='span'
                                color={theme.palette.primary.accent}>
                                User
                            </Typography>
                        </FormControl>
                        {principalType === PrincipalType.User && <UserProfileSearchDropdown
                            value={currentUser}
                            onChange={handleUserSelect}
                            hasError={nameInConflict}
                        />}
                        {principalType === PrincipalType.UserGroup && <EntitiesSearchableCommonDropDown
                            initialEntityOption={{ name: CREATE_NEW_USER_GROUP_OPTION_LABEL, id: CREATE_NEW_USER_GROUP_OPTION_LABEL, tenantId: props.tenantId, description: '', shouldDisplayCreateNewUserGroupFields: true } as UserGroupDropdownOption}
                            value={currentUserGroup}
                            onChange={handleUserGroupSelect}
                            label='User Group'
                            getOptionLabel={getOptionLabelDefaultCallback}
                            isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                            asyncThunkAction={fetchUserGroups}
                            asyncThunkArgs={{ tenantId: props.tenantId }}
                            displayMenuItemContent={displayEntityMenuItemContent}
                            filterOptionsCustomCallback={filterUserGroupOptionsCustomCallback}
                            effectDependecyArray={[props.tenantId]}
                        />}
                    </>
                }
                {props.aclPage !== AclEntriesPage.License && <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label={isGroup ? 'User Group Name' : 'Name'}
                    variant="filled"
                    defaultValue={props.principalName}
                    disabled
                />}
                {(currentUserGroup as UserGroupWithCreationFlag)?.shouldDisplayCreateNewUserGroupFields &&
                    <>
                        <TextField
                            inputRef={nameFieldRef}
                            id="filled-basic"
                            size="small"
                            label="User Group Name"
                            placeholder="Type User Group Name"
                            variant="filled"
                            focused
                            autoFocus={true}
                            onChange={handleSaveBtnEnabled}
                            InputProps={{
                                disableUnderline: nameInConflict && (currentUserGroup as UserGroupWithCreationFlag)?.shouldDisplayCreateNewUserGroupFields && !!nameFieldRef?.current?.value.trim()
                            }}
                            sx={editDrawerTextFieldStyles(nameInConflict && (currentUserGroup as UserGroupWithCreationFlag)?.shouldDisplayCreateNewUserGroupFields && !!nameFieldRef?.current?.value.trim())}
                        />
                        <TextField
                            inputRef={descriptionFieldRef}
                            id="filled-basic"
                            size="small"
                            label="User Group Description"
                            placeholder="Type User Group Description"
                            variant="filled"
                            focused
                            sx={editDrawerTextFieldStyles(false)}
                        />
                    </>
                }
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={props.tenantName}
                    disabled
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        {`${conflictErrorOrigin} already exists.`}
                    </Box>
                }
            </Stack>
        </EditDrawer>,
    [
        props,
        isGroup,
        theme,
        currentAclResource,
        currentAclAccess,
        currentAclConditionKey,
        currentAclConditionValue,
        currentLicense,
        currentUser,
        currentUserGroup,
        principalType,
        baseState,
        handleAclResourceSelect,
        handleAclAccessSelect,
        handleConditionKeyChange,
        handleLicenseSelect,
        handleUserSelect,
        handleUserGroupSelect,
        principalTypeChangeHandler,
        handleSaveBtnEnabled
    ]);

    return {
        ...baseState,
        handleSaveBtnEnabled,
        currentAclResource,
        setCurrentAclResource,
        currentAclAccess,
        setCurrentAclAccess,
        currentAclConditionKey,
        setCurrentAclConditionKey,
        currentAclConditionValue,
        setCurrentAclConditionValue,
        handleConditionKeyChange,
        currentLicense,
        setCurrentLicense,
        currentUser,
        setCurrentUser,
        currentUserGroup,
        setCurrentUserGroup,
        principalType,
        setPrincipalType,
        nameFieldRef,
        descriptionFieldRef,
        onOpenChange,
        handleAclResourceSelect,
        handleAclAccessSelect,
        handleLicenseSelect,
        principalTypeChangeHandler,
        handleUserSelect,
        handleUserGroupSelect,
        getOptions,
        getContent
    };
}

export interface EditStateHandlerResult extends BaseStateHandlerResult {
    editingLicense: License | null;
    setEditingLicense: (license: License | null) => void;
    conditionValues: Record<string, Record<string, string>>;
    setConditionValues: (values: Record<string, Record<string, string>>) => void;
    onOpenChange: (isOpen: boolean) => void;
    getOptions: () => object;
    getLicenseName: () => string;
    getContent: (
        opCallback: () => void,
        processing: boolean,
        nameInConflict: boolean
    ) => JSX.Element;
}

export function useEditStateHandler(
    props: AclEntriesDrawerProperties,
    item: AclFormatted
): EditStateHandlerResult {
    const dispatch = useAppDispatch();
    const baseState = useBaseStateHandler(props);
    const aclPage = props.aclPage;

    const [editingLicense, setEditingLicense] = useState<License | null>(null);
    const [conditionValues, setConditionValues] = useState<Record<string, Record<string, string>>>({});

    const isConditionValid = useCallback((): boolean => {
        if (Object.keys(conditionValues).length === 0) {
            return true;
        }

        for (const conditionKey of Object.keys(conditionValues)) {
            const conditionValue = conditionValues[conditionKey];
            const hasValue = conditionValue !== undefined &&
                Object.values(conditionValue).some(val => val !== undefined && val !== '');

            if (!hasValue) {
                return false;
            }

            const validator = CONDITION_VALIDATORS[conditionKey];
            if (validator) {
                const value = conditionValue?.[validator.parameterName];
                if (!validator.validate(value)) {
                    return false;
                }
            }
        }
        return true;
    }, [conditionValues]);

    const handleSaveBtnEnabled = useCallback(() => {
        let isButtonEnabled = !baseState.datesValidation.hasError && isConditionValid();
        if (aclPage !== AclEntriesPage.License) {
            isButtonEnabled &&= props.tenantRequiresLicense ? !! item?.licenseId : true;
        }
        dispatch(setSaveButtonEnabled(isButtonEnabled));
    }, [dispatch, baseState.datesValidation.hasError, aclPage, item?.licenseId, isConditionValid]);

    useEffect(() => {
        handleSaveBtnEnabled();
    }, [baseState.datesValidation, conditionValues, handleSaveBtnEnabled]);

    const onOpenChange = useCallback((isOpen: boolean) => {
        baseState.resetDates(dateToBackendFormat(item?.startDate), dateToBackendFormat(item?.endDate));
        setEditingLicense(null);
        // Initialize condition values from item's existing conditions
        if (item?.conditions && item.conditions.length > 0) {
            const initialConditionValues: Record<string, Record<string, string>> = {};
            for (const condition of item.conditions) {
                initialConditionValues[condition.conditionKey] = condition.parameters ?? {};
            }
            setConditionValues(initialConditionValues);
        } else {
            setConditionValues({});
        }
        if (isOpen) {
            handleSaveBtnEnabled();
            if (aclPage !== AclEntriesPage.License && !editingLicense) {
                const getParams = {
                    tenantId: item?.tenantId ?? '',
                    id: item?.licenseId ?? ''
                };
                if(props.tenantRequiresLicense){
                    dispatch(fetchLicense(getParams))
                        .unwrap()
                        .then((result: License) => {
                            setEditingLicense(result);
                        });
                }
            }
        }
    }, [baseState, dispatch, item, aclPage, editingLicense, handleSaveBtnEnabled]);

    const getOptions = useCallback(() => {
        let paginationInfo: AclSearchPaginationInfo = {};
        const putOptions: APIAclEntryPostParams = {
            tenantId: item.tenantId,
            accessId: item.accessId,
            resourceId: item.resourceId,
            userId: item.userId,
            usergroupId: item.usergroupId,
            licenseId: item.licenseId,
            startDate: baseState.startDate,
            endDate: baseState.endDate ? baseState.endDate : undefined,
            tenantRequiresLicense: props.tenantRequiresLicense
        };
        if (props.aclPage === AclEntriesPage.License) {
            paginationInfo = {
                skip: baseState.currentPage * baseState.rowsPerPage,
                take: baseState.rowsPerPage,
                search: baseState.filterProp.keywords[0],
                sortKey: baseState.sortModel[0].field,
                sortOrder: baseState.sortModel[0].sort
            };
        }

        // Set condition values if any exist
        if (Object.keys(conditionValues).length > 0) {
            putOptions.conditions = Object.entries(conditionValues).map(([conditionKey, parameters]) => ({
                conditionKey,
                parameters
            }));
        }

        return {
            item: putOptions,
            paginationInfo: paginationInfo,
            aclPage: aclPage
        };
    }, [
        item,
        props,
        aclPage,
        conditionValues,
        baseState.startDate,
        baseState.endDate,
        baseState.currentPage,
        baseState.rowsPerPage,
        baseState.filterProp.keywords,
        baseState.sortModel
    ]);

    const getLicenseName = useCallback(() => {
        if (aclPage === AclEntriesPage.License) {
            return props.licenseBillingSystemName + ' - ' + props.licenseBillingRecord;
        }
        if (editingLicense) {
            return editingLicense.billingSystemName + ' - ' + editingLicense.billingRecord;
        }
        return '';
    }, [aclPage, props.licenseBillingSystemName, props.licenseBillingRecord, editingLicense]);

    const getContent = useCallback((
        putCallback: () => void,
        processing: boolean,
        nameInConflict: boolean
    ) =>
        <EditDrawer
            title='ACL Entry'
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="ACL Resource"
                    variant="filled"
                    value={item?.resourceName ?? ''}
                    disabled
                />
                {Object.keys(conditionValues).length > 0 && (
                    <AclConditionsEditDropDown
                        conditions={conditionValues}
                        onChange={setConditionValues}
                    />
                )}
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="ACL Access"
                    variant="filled"
                    value={item?.accessName ?? ''}
                    disabled
                />
                {props.tenantRequiresLicense && <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="License"
                    variant="filled"
                    value={getLicenseName()}
                    disabled
                />}
                {props.tenantRequiresLicense && <Stack
                    display="flex"
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <PalmDatePicker
                            label='Start Date'
                            required
                            onChange={(value?: string) => baseState.setStartDate(value)}
                            value={baseState.startDate}
                        ></PalmDatePicker>
                        <PalmDatePicker
                            label='End Date'
                            onChange={(value?: string) => baseState.setEndDate(value)}
                            value={baseState.endDate}
                        ></PalmDatePicker>
                    </LocalizationProvider>
                </Stack>}
                {baseState.datesValidation.hasError && (
                    <ErrorHandler
                        component='div'
                        display='inline'
                        errorMessage={baseState.datesValidation.errorMessage}
                    />
                )}
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label={item?.usergroupId ? 'User Group Name' : 'User Name'}
                    variant="filled"
                    defaultValue={aclPage === AclEntriesPage.License
                        ? item?.principalName
                        : props.principalName}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={props.tenantName}
                    disabled
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        ACL Entry already exists.
                    </Box>
                }
            </Stack>
        </EditDrawer>,
    [item, props, aclPage, baseState, conditionValues, getLicenseName]);

    return {
        ...baseState,
        handleSaveBtnEnabled,
        editingLicense,
        setEditingLicense,
        conditionValues,
        setConditionValues,
        onOpenChange,
        getOptions,
        getLicenseName,
        getContent
    };
}
